import { z } from "zod";

import { baseApiResponse } from "./BaseApiResponse";
import { Currencies } from "./Currency";

export enum SportBetsType {
	SINGLE = "single",
	MULTI = "multi",
	GOLIATH = "goliath",
	HEINZ = "heinz",
	LUCKY15 = "lucky15",
	LUCKY31 = "lucky31",
	LUCKY63 = "lucky63",
	PATENT = "patent",
	SUPER_HEINZ = "super-heinz",
	SUPER_YANKEE = "super-yankee",
	SYSTEM = "system",
	TRIXIE = "trixie",
	YANKEE = "yankee"
}

export enum SportBetsStatus {
	ACCEPTED = "accepted",
	CLOSED = "closed",
	CANCELLED = "cancelled",
	ROLLBACK = "rollback",
	CASHOUT = "cashout"
}

export enum SportBetsResult {
	WIN = "win",
	LOSE = "lose"
}

export enum SportBetsOrder {
	ID_ASC = "id",
	ID_DESC = "-id",
	CREATED_AT_ASC = "createdAt",
	CREATED_AT_DESC = "-createdAt"
}

export enum GameActionType {
	BET = "bet",
	WIN = "win",
	ZERO_CREDIT = "zerocredit"
}

export const betsResolver = z.object({
	Action: z.string(),
	Amount: z.string(),
	Currency: z.string(),
	Date: z.string(),
	DateISO: z.string(),
	GameID: z.string(),
	GameName: z.string(),
	Merchant: z.string()
});

export type BetsModel = z.infer<typeof betsResolver>;

export const sportBetsEventResolver = z.object({
	id: z.string(),
	result: z.string(),
	odds: z.number(),
	entityType: z.string(),
	outcomeName: z.string(),
	marketName: z.string(),
	teamHomeName: z.string(),
	teamAwayName: z.string(),
	seasonName: z.string(),
	tournamentName: z.string(),
	categoryName: z.string(),
	sportName: z.string(),
	eventStartDate: z.string(),
	teamHomeScore: z.number(),
	teamAwayScore: z.number()
});

export type SportBetsEventModel = z.infer<typeof sportBetsEventResolver>;

export const sportBetsResolver = z.object({
	id: z.string(),
	type: z.nativeEnum(SportBetsType),
	systemBet: z.string(),
	status: z.nativeEnum(SportBetsStatus),
	result: z.nativeEnum(SportBetsResult),
	amount: z.number(),
	possibleWin: z.number(),
	payout: z.number(),
	createdAt: z.number(),
	odds: z.number(),
	events: sportBetsEventResolver.array()
});

export type SportBetsModel = z.infer<typeof sportBetsResolver>;

export const getListBetsRequestParamsResolver = z.object({
	startDate: z.string(),
	endDate: z.string(),
	type: z.string().optional()
});

export type GetListBetsRequestParamsModel = z.infer<typeof getListBetsRequestParamsResolver>;

export const getListSportBetsRequestParamsResolver = z.object({
	dateStart: z.string().optional(),
	dateEnd: z.string().optional(),
	type: z.nativeEnum(SportBetsType).optional(),
	status: z.nativeEnum(SportBetsStatus).optional(),
	result: z.nativeEnum(SportBetsResult).optional(),
	limit: z.number().int().optional(),
	offset: z.number().int().optional(),
	order: z.nativeEnum(SportBetsOrder).optional()
});

export type GetListSportBetsRequestParamsModel = z.infer<typeof getListSportBetsRequestParamsResolver>;

export const listBetsResult = baseApiResponse.omit({ data: true }).extend({
	data: betsResolver.array()
});

export type ListBetsResult = z.infer<typeof listBetsResult>;

export const listSportBetsResult = baseApiResponse.omit({ data: true }).extend({
	data: sportBetsResolver.array()
});

export type ListSportBetsResult = z.infer<typeof listSportBetsResult>;

export const lastGamesActionResolver = z.object({
	BetAmount: z.number(),
	BetAmountEUR: z.number(),
	Amount: z.number(),
	AmountEUR: z.number(),
	Currency: z.nativeEnum(Currencies),
	GameID: z.number(),
	GameName: z.string(),
	Type: z.nativeEnum(GameActionType),
	Multiplier: z.number(),
	MaskedUserName: z.string()
});

export type LastGamesActionModel = z.infer<typeof lastGamesActionResolver>;

export const lastGamesActionsResult = baseApiResponse.omit({ data: true }).extend({
	data: lastGamesActionResolver.array()
});

export type LastGamesActionsResult = z.infer<typeof lastGamesActionsResult>;
